import { ChevronLeftIconRounded } from '@shared/assets/icons';
import { SminexClubGroup, useAuth, useJwtClaims, hasSminexClubAccess } from '@features/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { FiltersProvider } from './lib/filters-context';
import { useFetchEvent } from './api/useFetchEvent';
import { IconButton } from '@shared/ui/icon-button';
import { EventStatistics } from '@entities/event';
import { useToast } from '@shared/hooks/useToast';
import { EventSminexClub } from '@widgets/events';
import { EventBody } from './ui/event-body';
import { AccessDenied } from './ui/access-denied';
import { isEventInGroup } from '@shared/lib';
import stylex from '@stylexjs/stylex';
import { FC, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { styles } from './styles';
import { EventSendInvitationsScreen } from '@pages/event-send-invitations';
import type { EventSendInvitationsInput, EventSendInvitationsSelection } from '@pages/event-send-invitations/lib/types';
import { EventSendTicketsScreen } from '@pages/event-send-tickets';
import type { EventSendTicketsInput, EventSendTicketsSelection } from '@pages/event-send-tickets/lib/types';
import useGetSettingsByEventId from '@features/api/use-get-settings';
import { SendContentType, useSendMessages, type SendMessagesInput } from './api/useSendMessages';
import {
  useSendInvitationMessages,
  type SendInvitationMessagesInput,
} from '@pages/event-send-invitations/api/useSendInvitationMessages';
import { useGetSendingMessageCount } from './api/useGetSendingMessageCount';
import {
  clearAllInviteSessionData,
  clearInviteFormData,
  clearSelectedEmployees,
  hasMeaningfulInviteFormData,
  loadInviteFormData,
  loadSelectedEmployees,
  saveSelectedEmployees,
} from '@pages/event-send-invitations/lib/invite-session-storage';
import { useInviteSelectedEmployees } from '@pages/event-send-invitations/lib/use-invite-selected-employees';

export const EventPage: FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id } = useParams();
  const { event, error, refetch } = useFetchEvent(Number(id));
  const [sendInvitationsSelection, setSendInvitationsSelection] = useState<EventSendInvitationsSelection | null>(null);
  const [pendingSendInvitations, setPendingSendInvitations] = useState<EventSendInvitationsInput | null>(null);
  const [sendTicketsSelection, setSendTicketsSelection] = useState<EventSendTicketsSelection | null>(null);
  const [pendingSendTickets, setPendingSendTickets] = useState<EventSendTicketsInput | null>(null);
  const [isSettingsPopupVisible, setIsSettingsPopupVisible] = useState(false);
  const eventId = id === undefined ? 0 : Number(id);
  const { checkedUsers, setCheckedUsers, clearCheckedUsers } = useInviteSelectedEmployees(eventId);
  const { data: eventSettings, refetch: refetchEventSettings } = useGetSettingsByEventId(eventId);
  const { sendMessages: sendInvitationMessagesMutation } = useSendInvitationMessages();
  const { sendMessages: sendTicketMessagesMutation } = useSendMessages();
  const {
    sendingMessageCount,
    error: sendingMessageCountError,
    fetching: isSendingMessageCountFetching,
  } = useGetSendingMessageCount(
    pendingSendInvitations
      ? { eventId, contentType: SendContentType.Invite, ticketIds: pendingSendInvitations.ticketIds }
      : null
  );
  const {
    sendingMessageCount: sendingTicketMessageCount,
    error: sendingTicketMessageCountError,
    fetching: isSendingTicketMessageCountFetching,
  } = useGetSendingMessageCount(
    pendingSendTickets
      ? { eventId, contentType: SendContentType.Ticket, ticketIds: pendingSendTickets.ticketIds }
      : null
  );

  const { data } = useAuth();
  const { groupId, permissions } = useJwtClaims();
  const isSminexClubEvent = isEventInGroup(event?.getEventById?.groups, SminexClubGroup, data.groupName);
  const canAccessSminexClub = hasSminexClubAccess(groupId, permissions);
  const isSendScreenOpen = sendInvitationsSelection !== null || sendTicketsSelection !== null;

  useEffect(() => {
    if (error) {
      showToast('Нет соединения с сервером. Проверьте VPN или интернет', 'error', {
        duration: 10000,
        closable: true,
      });
    }
  }, [error]);

  useLayoutEffect(() => {
    if (!eventId) {
      return;
    }

    const restored = loadSelectedEmployees(eventId);
    const savedForm = loadInviteFormData(eventId);
    if (!hasMeaningfulInviteFormData(savedForm) || !restored || !Object.keys(restored.checkedUsers).length) {
      return;
    }

    setPendingSendInvitations({
      ticketIds: Object.keys(restored.checkedUsers).map(Number),
      userIds: Object.values(restored.checkedUsers),
    });
  }, [eventId]);

  useEffect(() => {
    if (!pendingSendInvitations || isSendingMessageCountFetching) {
      return;
    }

    if (sendingMessageCountError || !sendingMessageCount) {
      showToast('Не удалось получить данные для отправки приглашений', 'error');
      setPendingSendInvitations(null);
      return;
    }

    if (!sendingMessageCount.ticketIds.length) {
      showToast('Нет участников, доступных для отправки приглашений', 'error');
      setPendingSendInvitations(null);
      return;
    }

    setSendInvitationsSelection({
      ...pendingSendInvitations,
      ...sendingMessageCount,
    });
    setPendingSendInvitations(null);
  }, [pendingSendInvitations, isSendingMessageCountFetching, sendingMessageCountError, sendingMessageCount, showToast]);

  useEffect(() => {
    if (!pendingSendTickets || isSendingTicketMessageCountFetching) {
      return;
    }

    if (sendingTicketMessageCountError || !sendingTicketMessageCount) {
      showToast('Не удалось получить данные для отправки билетов', 'error');
      setPendingSendTickets(null);
      return;
    }

    if (!sendingTicketMessageCount.ticketIds.length) {
      showToast('Нет участников, доступных для отправки билетов', 'error');
      setPendingSendTickets(null);
      return;
    }

    setSendTicketsSelection({
      ...pendingSendTickets,
      ...sendingTicketMessageCount,
    });
    setPendingSendTickets(null);
  }, [
    pendingSendTickets,
    isSendingTicketMessageCountFetching,
    sendingTicketMessageCountError,
    sendingTicketMessageCount,
    showToast,
  ]);

  const handleSendMessages = async (input: SendInvitationMessagesInput) => {
    const result = await sendInvitationMessagesMutation(input);
    if (result.error) {
      showToast('Произошла ошибка при отправке приглашений', 'error');
      throw result.error;
    }
    return result.data?.sendMessages.attempt;
  };

  const handleSendTicketMessages = async (input: SendMessagesInput) => {
    const result = await sendTicketMessagesMutation(input);
    if (result.error) {
      showToast('Произошла ошибка при отправке билетов', 'error');
      throw result.error;
    }
    return result.data?.sendMessages.attempt;
  };

  const handleInvitationsSent = () => {
    clearAllInviteSessionData();
    clearCheckedUsers();
    setSendInvitationsSelection(null);
    refetch({ requestPolicy: 'network-only' });
    refetchEventSettings();
  };

  const handleTicketsSent = () => {
    clearCheckedUsers();
    setSendTicketsSelection(null);
    refetch({ requestPolicy: 'network-only' });
  };

  const handleOpenSettings = () => setIsSettingsPopupVisible(true);
  const handleCloseSettings = () => setIsSettingsPopupVisible(false);
  const handleCloseSendInvitationsScreen = () => {
    clearInviteFormData();
    clearSelectedEmployees();
    setSendInvitationsSelection(null);
  };
  const handleCloseSendTicketsScreen = () => {
    setSendTicketsSelection(null);
  };
  const handleNavigateBack = () => navigate(-1);

  const handleOpenSendInvitationsScreen = useCallback(
    (selection: EventSendInvitationsInput) => {
      const checkedUsersFromSelection = Object.fromEntries(
        selection.ticketIds.map((ticketId, index) => [ticketId, selection.userIds[index]])
      );
      saveSelectedEmployees({ eventId, checkedUsers: checkedUsersFromSelection });
      setPendingSendInvitations(selection);
    },
    [eventId]
  );

  const handleOpenSendTicketsScreen = useCallback((selection: EventSendTicketsInput) => {
    setPendingSendTickets(selection);
  }, []);

  if (isSminexClubEvent) {
    if (!canAccessSminexClub) {
      return <AccessDenied onBack={() => navigate(-1)} />;
    }
    return <EventSminexClub event={event?.getEventById} />;
  }

  return (
    <FiltersProvider>
      {sendInvitationsSelection && (
        <EventSendInvitationsScreen
          onBack={handleCloseSendInvitationsScreen}
          eventId={eventId}
          eventName={event?.getEventById.name ?? null}
          eventDateStart={event?.getEventById.dateStart ?? null}
          eventDateEnd={event?.getEventById.dateEnd ?? null}
          eventSettings={eventSettings ?? null}
          ticketIds={sendInvitationsSelection.ticketIds}
          emailWillBeSent={sendInvitationsSelection.emailWillBeSent}
          smsWillBeSent={sendInvitationsSelection.smsWillBeSent}
          vkWillBeSent={sendInvitationsSelection.vkWillBeSent}
          totalInvites={event?.getEventById.metadata?.totalInvites ?? 0}
          sendMessages={handleSendMessages}
          onSent={handleInvitationsSent}
        />
      )}
      {sendTicketsSelection && (
        <EventSendTicketsScreen
          onBack={handleCloseSendTicketsScreen}
          eventId={eventId}
          eventName={event?.getEventById.name ?? null}
          eventDateStart={event?.getEventById.dateStart ?? null}
          ticketIds={sendTicketsSelection.ticketIds}
          emailWillBeSent={sendTicketsSelection.emailWillBeSent}
          smsWillBeSent={sendTicketsSelection.smsWillBeSent}
          vkWillBeSent={sendTicketsSelection.vkWillBeSent}
          sendMessages={handleSendTicketMessages}
          onSent={handleTicketsSent}
        />
      )}
      <div {...stylex.props(styles.container, isSendScreenOpen && styles.mainHidden)}>
        <div {...stylex.props(styles.sidebar)}>
          <IconButton onClick={handleNavigateBack} icon={<ChevronLeftIconRounded />} variant="primary" size="small">
            Назад
          </IconButton>

          <EventStatistics
            ticketsMetadata={event?.getEventById.ticketsMetadata}
            metadata={event?.getEventById.metadata}
            eventDateStart={event?.getEventById.dateStart}
          />
        </div>
        <EventBody
          event={event}
          refetchEvent={refetch}
          onOpenSendInvitationsScreen={handleOpenSendInvitationsScreen}
          onOpenSendTicketsScreen={handleOpenSendTicketsScreen}
          isSettingsPopupVisible={isSettingsPopupVisible}
          onOpenSettings={handleOpenSettings}
          onCloseSettings={handleCloseSettings}
          checkedUsers={checkedUsers}
          setCheckedUsers={setCheckedUsers}
        />
      </div>
    </FiltersProvider>
  );
};
